/* globals customElements */

import { LitElement, html, css } from 'lit';

const messages = {
  good: '🏆 Uses Design Tokens!',
  'good-external': 'ℹ️ Design Token Reference from an external file.',
  warn: `☑️ This value is ignored`,
  'bad-excludedByStylelint':
    'ℹ️ Stylelint-disabled: but has no valid token for this property.',
  'good-excludedByStylelint':
    'ℹ️ Stylelint-disabled: but may actually be valid.',
  'warn-excludedByStylelint':
    'ℹ️ Stylelint-disabled: but looks to be an ignored value.',
  excludedByStylelint: html`This line is excluded by
    <code
      >/* stylelint-disable-next-line stylelint-plugin-mozilla/use-design-tokens
      */</code
    >.`,
  bad: '❌ Not currently using a valid design token for this property.',
};

/**
 * `<token-tooltip>` is a custom element for displaying contextual information
 * about CSS design token usage.
 *
 * It renders:
 * - A status message based on whether tokens are used or not, or if the property value is ignored.
 * - A list of design tokens used.
 * - A trace of variable resolution.
 * - The source file(s) involved,
 * - Any unresolved variables.
 *
 * Intended for use as a floating tooltip element, e.g. in a code viewer.
 */
export class TokenTooltip extends LitElement {
  static properties = {
    // Token usage status: "good", "warn", or "bad".
    // Controls the icon/message shown at the top of the tooltip.
    status: { type: String },
    // Trace of CSS variable resolution steps, e.g., ['var(--a)', 'var(--b)', '12px'].
    trace: { type: Array },
    // List of source file paths where the tokens were defined.
    source: { type: Array },
    // List of unresolved CSS variables (e.g., ['--missing']).
    unresolved: { type: Array },
    // List of design tokens identified in the value.
    tokens: { type: Array },
    // resolutionType: 'local' or 'external'
    resolutionType: { type: String },
    isExcludedByStylelint: { type: Boolean },
  };

  constructor() {
    super();
    this.status = 'bad';
    this.trace = [];
    this.tokens = [];
    this.source = [];
    this.unresolved = [];
  }

  static styles = css`
    :host {
      position: absolute;
      z-index: 1000;
      background: #fff;
      border: 1px solid #ccc;
      padding: 0.75rem;
      font-size: 0.75rem;
      border-radius: 6px;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      white-space: nowrap;
      overflow-x: auto;
      width: min-content;
      max-width: 90vw;
    }

    :host(.wrap) {
      white-space: normal;
      word-break: break-word;
      overflow-wrap: break-word;
    }

    ul,
    li {
      margin: 0.25em 0 0.25em 1em;
      padding: 0;
    }

    .note {
      margin-top: 0.5em;
      padding: 0.25em 1em;
      border-radius: 0.75em;
      border: 1px solid gold;
      background: var(--status-warn-background);
      font-size: 0.75rem;
      font-style: italic;

      p {
        text-wrap: wrap;
      }

      code {
        color: green;
        font-size: 0.7rem;
      }
    }

    .status {
      font-weight: bold;
    }

    .label {
      font-weight: bold;
      margin-top: 0.5rem;
      display: block;
    }

    .block {
      margin-top: 0.25rem;
    }

    .label + ul {
      margin-top: 0;
    }
  `;

  /**
   * Returns a status message based on status and resolution type.
   *
   * If the status is "good" and the resolution type is "external",
   * a specialised message is returned. Otherwise, the message is
   * resolved from the status map, defaulting to "bad" if the status
   * is unknown.
   *
   * @param {string} status The status key, e.g. "good", "bad"
   * @returns {string} The corresponding status message
   */
  getStatusMessage(status) {
    return messages[status] || messages.bad;
  }

  /**
   * Renders the tooltip content dynamically based on the component's properties.
   * @returns {import('lit').TemplateResult} The rendered HTML content.
   */
  render() {
    return html`
      <div aria-live="polite">
        <div class="status" data-status=${this.status}>
          ${this.getStatusMessage(this.status)}
        </div>
        ${this.isExcludedByStylelint
          ? html`
              <div class="note">
                <p>${messages.excludedByStylelint}</p>
                ${this.status === 'good-external'
                  ? html`<p>
                      This is likely to have been excluded since the stylelint
                      rules only process local vars.
                    </p>`
                  : ''}
                ${this.status.startsWith('good')
                  ? html`<p>This line is still counted as a valid token.</p>`
                  : ''}
              </div>
            `
          : ''}
        ${this.status === 'good-external' && !this.isExcludedByStylelint
          ? html`<div class="note">
              <p>
                Note: Since var resolution points to an external file, the
                <code>use-design-tokens</code> rule may report an error because
                it is only aware of local variables. A suggestion would be to
                consider adding a comment to disable the specific line with:
                <br /><code
                  >/* stylelint-disable-next-line
                  stylelint-plugin-mozilla/use-design-tokens -- [Reason]
                  */</code
                ></p>
                <p>This line is still counted as a valid token.</p>
              </p>
            </div>`
          : ''}
        ${this.status.startsWith('good') && this.tokens.length
          ? html`
              <div class="label">
                🎨 Valid Design Tokens Used for this prop:
              </div>
              <ul>
                ${this.tokens.map(
                  (token) => html`<li><code>${token}</code></li>`,
                )}
              </ul>
            `
          : ''}
        ${this.trace.length > 1
          ? html`
              <div class="label">🔬 Trace:</div>
              ${this.renderTraceTree(this.trace)}
            `
          : ''}
        ${this.source.length
          ? html`
              <div class="label">📁 Source(s):</div>
              <ul>
                ${this.source.map((src) => html`<li><code>${src}</code></li>`)}
              </ul>
            `
          : ''}
        ${this.unresolved.length
          ? html`
              <div class="label">⚠️ Unresolved Vars:</div>
              <ul>
                ${this.unresolved.map(
                  (unresolvedVar) =>
                    html`<li><code>${unresolvedVar}</code></li>`,
                )}
              </ul>
            `
          : ''}
      </div>
    `;
  }

  /**
   * Recursively renders a nested unordered list representing a variable resolution trace.
   * @param {string[]} steps - The resolution steps (e.g., ['var(--a)', 'var(--b)', '12px']).
   * @returns {import('lit').TemplateResult|null}
   */
  renderTraceTree(steps = []) {
    if (steps.length === 0) {
      return null;
    }

    const [head, ...rest] = steps;
    return html`
      <ul>
        <li>
          <code>${head}</code>
          ${rest.length ? this.renderTraceTree(rest) : ''}
        </li>
      </ul>
    `;
  }
}

customElements.define('token-tooltip', TokenTooltip);
